import type {
  ReportCatalog,
  ReportQuery,
  ReportQueryResult
} from "@gettactic/api";
import { useQueries } from "@tanstack/react-query";
import { api } from "@/lib/api/api";
import { useReportQueryKey } from "./useReportQuery";

/** Dimensions worth asking "who accounts for this?" about, in preference order. */
const CONTRIBUTOR_DIMENSIONS = [
  "office",
  "team",
  "zone",
  "category",
  "resource_type",
  "floor",
  "space",
  "weekday",
  "status",
  "approval_status"
];
const MAX_DIMENSIONS = 3;
const TOP_MEMBERS = 3;

export type Contributor = {
  dimension: string;
  dimensionLabel: string;
  memberId: string;
  label: string;
  value: number;
  /** Share of the total, 0..1; null for ratios (shares don't add up). */
  share: number | null;
};

export type ContributorGroup = {
  dimension: string;
  dimensionLabel: string;
  contributors: Contributor[];
};

const run = async (query: ReportQuery): Promise<ReportQueryResult> => {
  const { result } = await api.client.reporting.query(query);
  if (!result) {
    throw new Error("empty report query response");
  }
  return result;
};

/**
 * "What's behind this number": for the first free dimensions of the query,
 * the top members and their share of the total. Runs one breakdown per
 * dimension, cached like any other query.
 */
export const useContributors = (
  catalog: ReportCatalog,
  query: ReportQuery,
  total: number | undefined
): { groups: ContributorGroup[]; isLoading: boolean } => {
  const measure = catalog.measures.find((m) => m.key === query.measure);
  const used = new Set([
    ...(query.breakdown ?? []),
    ...(query.filters ?? []).map((f) => f.dimension)
  ]);
  const dimensions = CONTRIBUTOR_DIMENSIONS.filter(
    (d) => measure?.dimensions.includes(d) && !used.has(d)
  ).slice(0, MAX_DIMENSIONS);
  const isRatio = measure?.kind === "percent";

  const results = useQueries({
    queries: dimensions.map((dimension) => {
      const breakdown: ReportQuery = {
        ...query,
        breakdown: [dimension],
        compare: undefined
      };
      return {
        queryKey: [useReportQueryKey, breakdown],
        queryFn: () => run(breakdown),
        staleTime: 60 * 1000
      };
    })
  });

  const groups: ContributorGroup[] = [];
  results.forEach((r, index) => {
    if (!r.data) {
      return;
    }
    const dimension = dimensions[index];
    const dimensionLabel =
      catalog.dimensions.find((d) => d.key === dimension)?.label ?? dimension;
    const rows = [...r.data.rows]
      .sort((a, b) => b.value - a.value)
      .slice(0, TOP_MEMBERS);
    if (rows.length === 0) {
      return;
    }
    groups.push({
      dimension,
      dimensionLabel,
      contributors: rows.map((row) => ({
        dimension,
        dimensionLabel,
        memberId: row.keys[0],
        label: row.labels[0],
        value: row.value,
        share:
          !isRatio && total && total > 0
            ? Math.max(0, Math.min(1, row.value / total))
            : null
      }))
    });
  });

  return { groups, isLoading: results.some((r) => r.isLoading) };
};
